/**
 * Read-only views over the brunel schema. Row-level security scopes every
 * query to the signed-in member's organizations. This module deliberately
 * exposes no insert/update/delete: browser writes go through guarded RPCs.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { MEANINGFUL_EVENT_TYPES, ROUTINE_EVENT_TYPES } from './audit';
import { canonicalWorkerTerminalRows, type RunningTaskProject } from './canonical-worker-rows';
import type { ObserverMessageRow, ObserverThreadRow } from './observer-view';
import type { AiConnectionRoleBindingRow } from './ai-role-bindings';
import type { AutopilotHeartbeatRow, AutopilotRoundRow } from '@brunel/shared';
import type { WorkforceSnapshot } from './workforce';

export interface Organization { readonly id: string; readonly name: string }
export interface Project { readonly org_id: string; readonly id: string; readonly name: string }
export interface Repository {
  readonly org_id: string; readonly project_id: string; readonly id: string;
  readonly name: string; readonly default_ref: string | null;
}
export interface Worker {
  readonly org_id: string; readonly id: string; readonly number: number; readonly name: string; readonly status: string;
  /** #650 set by retire_worker ("Discard Worker"). History keeps the row; active surfaces drop it. */
  readonly retired_at?: string | null;
}
export interface Mission { readonly org_id: string; readonly project_id: string; readonly id: string; readonly title: string; readonly status: string }
export interface OwnerCommand {
  readonly org_id: string; readonly id: string; readonly seq: number; readonly content: string;
  readonly project_id: string | null; readonly context_project_id?: string | null;
  readonly request_id?: string | null;
  readonly reply_targets?: readonly string[] | null; readonly worker_reply_targets?: readonly string[] | null;
  readonly observer_thread_id?: string | null;
  /** #651: when the server accepted it. Null on rows written before the column existed - unknown, never inferred. */
  readonly created_at?: string | null;
}

/** #644 Slice C: the exactly-once result of an owner message that was an exact existing control. */
export interface OwnerCommandControl {
  readonly org_id: string;
  readonly command_id: string;
  readonly action: 'retire_worker' | 'close_terminal' | 'stop_run' | 'retry_task' | 'open_lanes' | 'program_start' | 'program_pause' | 'program_resume' | 'program_stop' | 'program_steer' | 'reopen_task_completion' | 'stand_down' | 'resume_work';
  readonly state: 'completed' | 'accepted' | 'clarification' | 'refused';
  readonly reason: string;
  readonly summary: string;
  readonly worker_number: number | null;
  readonly created_at: string;
}

export interface OwnerCommandWorkerRecipient {
  readonly org_id: string;
  readonly owner_command_id: string;
  readonly recipient_kind: 'worker';
  readonly worker_id: string;
  readonly created_at: string;
}

export interface Approval { readonly org_id: string; readonly project_id: string; readonly id: string; readonly action: Record<string, unknown>; readonly requested_at?: string | null; readonly expires_at?: string | null }
export interface ApprovalDecision { readonly approval_id: string; readonly decision: 'approve' | 'deny'; readonly received_at: string }
export interface AuditEvent { readonly org_id: string; readonly id: string; readonly event_type: string; readonly reason_code: string | null; readonly deciding_gate: string | null; readonly occurred_at: string; readonly actor_id: string | null; readonly payload: Readonly<Record<string, unknown>> | null }
export interface ReportMessage { readonly org_id: string; readonly id: string; readonly stream_id: string; readonly seq: number; readonly envelope: { reportKind?: string; summary?: string }; readonly created_at: string }
export interface ChatStream { readonly org_id: string; readonly project_id: string; readonly id: string; readonly session_id: string | null }
export interface SessionRef { readonly org_id: string; readonly id: string; readonly worker_id: string; readonly project_id: string; readonly node_id: string; readonly status: string }
export interface WorkforceSession {
  readonly org_id: string; readonly id: string; readonly worker_id: string; readonly project_id: string;
  readonly node_id: string; readonly status: string; readonly lifecycle: string; readonly offering_key: string;
}
export interface NodeOffering { readonly org_id: string; readonly node_id: string; readonly offering_key: string }
export interface ModelOfferingRef {
  readonly org_id: string; readonly key: string; readonly runtime_key: string; readonly model_key: string;
  readonly execution_location: 'local' | 'cloud'; readonly provider_label: string;
}
export interface WorkerOfferingPreference {
  readonly org_id: string; readonly worker_id: string; readonly node_id: string;
  readonly offering_key: string; readonly updated_at: string;
}
/** #547 configured vs node-reported actual AI profile per current Worker session (brunel.worker_ai_execution_profiles). */
export interface WorkerAiExecutionProfileRow {
  readonly org_id: string; readonly worker_id: string; readonly node_id: string; readonly session_id: string;
  readonly mode: 'auto' | 'pinned';
  readonly configured_offering_key: string | null; readonly configured_runtime_key: string | null;
  readonly configured_model_key: string | null; readonly configured_effort_key: string | null;
  readonly configured_at: string | null;
  readonly actual_runtime_key: string | null; readonly actual_model_key: string | null;
  readonly actual_effort_key: string | null; readonly actual_reported_at: string | null;
  readonly busy: boolean;
}
/** #547 what each runtime on each node can actually apply (brunel.ai_runtime_capabilities). */
export interface AiRuntimeCapabilityRow {
  readonly org_id: string; readonly node_id: string; readonly runtime_key: string;
  readonly efforts: readonly string[]; readonly default_effort: string | null;
  readonly live_model_switch: boolean; readonly live_effort_switch: boolean;
  readonly evidence: string; readonly reported_at: string;
}
export interface CommandPlannerHostStatus {
  readonly org_id: string;
  readonly node_id: string;
  readonly node_name: string;
  readonly runtime_key: string;
  readonly fallback_mode: 'none' | 'api';
  readonly set_at: string;
  readonly last_seen_at: string | null;
  readonly connected: boolean;
  readonly offered: boolean;
  readonly runtime_available: boolean;
  readonly configured_offering_key: string | null;
  readonly configured_model_key: string | null;
  readonly configured_effort_key: string | null;
  readonly configured_at: string | null;
  readonly actual_runtime_key: string | null;
  readonly actual_model_key: string | null;
  readonly actual_effort_key: string | null;
  readonly actual_reported_at: string | null;
}

export interface RuntimeProof {
  readonly org_id: string; readonly node_id: string; readonly node_name: string;
  readonly runtime_key: string; readonly runtime_label: string | null; readonly offering_count: number;
  readonly run_count: number; readonly success_count: number; readonly failure_count: number; readonly proven: boolean;
  readonly last_run_at: string | null; readonly last_success_at: string | null; readonly last_failure_at: string | null;
  readonly last_success_summary: string | null; readonly last_failure_summary: string | null;
}

export interface TerminalSessionRow {
  readonly org_id: string; readonly id: string; readonly node_id: string; readonly node_name: string;
  readonly label: string; readonly shell: string | null; readonly cwd: string | null; readonly status: string;
  readonly pid: number | null; readonly columns_wanted: number; readonly rows_wanted: number;
  readonly requested_at: string; readonly opened_at: string | null; readonly last_activity_at: string | null;
  readonly close_requested_at: string | null; readonly closed_at: string | null; readonly exit_code: number | null;
  readonly failure: string | null; readonly live: boolean; readonly waiting_to_start: boolean;
}
export interface TerminalOutputRow {
  readonly org_id: string; readonly session_id: string; readonly seq: number; readonly at: string; readonly chunk: string;
}
/** What canonical storage holds of one run of a terminal's sequences: how many, and the highest. */
export interface StoredTerminalSequences {
  readonly stored: number; readonly newest: number | null;
}
export interface WorkerTerminalRow {
  readonly org_id: string; readonly worker_id: string; readonly worker_number: number;
  readonly worker_name: string; readonly worker_status: string;
  readonly session_id: string | null; readonly terminal_status: string | null; readonly pid: number | null;
  readonly shell: string | null; readonly cwd: string | null;
  readonly opened_at: string | null; readonly last_activity_at: string | null; readonly closed_at: string | null;
  readonly exit_code: number | null; readonly failure: string | null;
  readonly input_holder: string | null; readonly input_held: boolean | null;
  readonly input_holder_controller: string | null; readonly input_holder_controller_name: string | null;
  readonly node_id: string | null; readonly node_name: string | null;
  readonly workforce_session_id: string | null; readonly offering_key: string | null;
  readonly project_id: string | null; readonly lifecycle: string | null; readonly session_status: string | null;
  readonly project_name: string | null;
  readonly task_id: string | null; readonly task_title: string | null;
  readonly task_started_at: string | null; readonly task_deadline_at: string | null;
  /**
   * #736: which door opened this terminal - 'owner_manual' (open_terminal) or 'runtime'
   * (node_open_worker_terminal). Absent or null is Unknown and is never treated as manual.
   */
  readonly terminal_origin?: string | null;
}
export interface TerminalActionRow {
  readonly org_id: string; readonly id: number; readonly session_id: string; readonly at: string;
  readonly action: string; readonly source: string; readonly decision: string; readonly reason: string | null;
  readonly byte_count: number; readonly control_bytes: boolean; readonly controller_id: string | null;
}
export interface CommandPlanRow {
  readonly org_id: string; readonly id: string; readonly goal: string; readonly status: string;
  readonly verdict: string | null; readonly planner_key: string | null;
  readonly instruction: string | null; readonly success_criteria: string | null;
  readonly stop_conditions: string | null; readonly expected_observation: string | null;
  readonly requires_approval: boolean;
  readonly observation: string | null; readonly result_summary: string | null;
  readonly proposed_next_instruction: string | null; readonly failure: string | null;
  readonly previous_plan_id: string | null;
  readonly created_at: string; readonly drafted_at: string | null;
  readonly sent_at: string | null; readonly evaluated_at: string | null;
  readonly worker_id: string; readonly worker_number: number; readonly worker_name: string;
  readonly worker_status: string;
  readonly controller_id: string | null; readonly controller_name: string | null;
  readonly session_id: string | null; readonly terminal_status: string | null;
  readonly node_id: string | null; readonly node_name: string | null;
}
export interface ChatMessage {
  readonly org_id: string; readonly id: string; readonly stream_id: string; readonly seq: number;
  readonly content: string; readonly created_at: string;
  readonly author_kind?: 'owner' | 'worker' | 'supervisor';
  readonly worker_id?: string | null;
  readonly owner_command_id?: string | null;
  readonly author_identity?: Record<string, unknown> | null;
}

export interface NodePresence { readonly org_id: string; readonly node_id: string; readonly name: string; readonly status: string; readonly last_seen_at: string | null }
export interface NodeVersion {
  readonly org_id: string; readonly node_id: string; readonly name: string;
  readonly running_version: string | null; readonly target_version: string | null; readonly behind: boolean;
  readonly update_state: 'applied' | 'rolled_back' | 'failed' | null;
  readonly update_version: string | null; readonly update_detail: string | null;
  readonly update_reported_at: string | null; readonly last_seen_at: string | null;
}
export interface NodeRelease {
  readonly org_id: string; readonly version: string; readonly bundle_url: string; readonly bundle_sha256: string;
  readonly notes: string | null; readonly published_at: string;
}
/** The organization switch for automatic idle computer updates. No row means on. */
export interface NodeUpdateSettingsRow { readonly org_id: string; readonly automatic_when_idle: boolean }
export interface LaunchCatalogRow {
  readonly org_id: string; readonly runtime_key: string; readonly runtime_label: string | null;
  readonly position: number; readonly file: string; readonly args: readonly string[];
  readonly env: Readonly<Record<string, string>> | null; readonly needs_model: boolean;
  readonly updated_at: string; readonly updated_by: string | null;
}
export interface AutoRuleRow {
  readonly org_id: string; readonly id: string;
  readonly effect: 'auto_approve' | 'ignore_warning' | 'never_auto_approve' | 'ask_every_time';
  readonly project_id: string | null; readonly worker_id: string | null; readonly action_kind: string | null;
  readonly warning_code: string | null; readonly enabled: boolean; readonly expires_at: string | null;
  readonly severity: string | null; readonly learned: boolean;
}
/**
 * A permanent owner rule, exactly as the owner stated it. Root AGENTS.md is the canonical
 * owner contract; this is the copy Rules shows. Nothing in Command can add, edit or remove one.
 */
export interface OwnerRuleRow {
  readonly rule_key: string; readonly statement: string; readonly permanent: boolean;
  readonly stated_on: string; readonly source: string;
}
export interface ApprovalPolicyRow {
  readonly org_id: string; readonly mode: 'manual' | 'smart' | 'low' | 'low_medium' | 'custom' | 'autonomous';
  /** #805: 'auto' starts an owner-command plan on the owner's instruction; 'ask' waits for Go ahead. */
  readonly plan_approval?: 'auto' | 'ask';
  readonly updated_at: string;
}

export interface PlannedTaskView {
  readonly title: string; readonly instructions: string; readonly dependsOn?: readonly number[];
  /** The Worker this task was planned for. The only thing that actually routes it. */
  readonly preferredWorkerNumber?: number;
}

/** The standing routing instruction for one project, while the owner has one set. */
export interface WorkerRoutingRow {
  readonly org_id: string; readonly project_id: string; readonly project_name: string;
  readonly worker_number: number; readonly worker_name: string | null;
  readonly node_name: string | null; readonly set_at: string;
}
export interface Plan {
  readonly org_id: string; readonly project_id: string; readonly id: string; readonly command_id: string;
  readonly status: 'proposed' | 'approved' | 'rejected' | 'failed'; readonly summary: string;
  readonly tasks: readonly PlannedTaskView[]; readonly reason: string | null; readonly created_at: string;
  /** The mission the approved plan became, and the only honest link from a plan to its runs. */
  readonly mission_id: string | null;
  /** #644: who authorized it - the owner deciding, or the owner command itself (a direct instruction). */
  readonly authority?: 'owner_decision' | 'owner_command' | null;
  /** #644: why a direct instruction was not carried out from the command alone; Command asks about exactly this. */
  readonly held_for?: string | null;
  /** #656: the specifics behind held_for - which Worker and computer, what the runtime reported and until when, what holds the computer back. */
  readonly held_detail?: HeldDetail | null;
}

/** #656: plans.held_detail as the server writes it. Every field is optional; an absent one is left unsaid. */
export interface HeldDetail {
  readonly workerNumber?: number;
  readonly machine?: string;
  readonly runtime?: string;
  readonly outage?: 'out_of_usage' | 'signed_out' | 'misconfigured';
  readonly availableFrom?: string;
  readonly holdingBack?: 'seats' | 'memory' | 'video_memory' | 'cpu' | 'graphics' | 'runtime_memory' | 'disk';
  /** A Worker question the owner can answer here: the Workers that can take this plan, readiest first. */
  readonly choices?: readonly WorkerChoice[];
  /** Why no Worker could take a named AI. metered_only: the only offering is a metered route, which is not used. */
  readonly limitation?: string;
}

/** One Worker the owner can choose to continue a held plan, by its stable number. */
export interface WorkerChoice {
  readonly workerNumber: number;
  readonly name?: string;
  readonly machine?: string;
  readonly runtime?: string;
}

/**
 * A task as it exists once a plan has been approved: the row the runs point at.
 * It is what lets the transcript say what became of an approved plan without
 * guessing - the plan names its mission, the mission owns these, and a finished
 * run names one of them.
 */
export interface MissionTask {
  readonly org_id: string; readonly project_id: string; readonly id: string;
  readonly mission_id: string; readonly title: string; readonly status: string;
}

/**
 * How Brunel ended the wait of a job whose named Worker could not take it
 * (`brunel.waiting_job_recoveries`): its work had already landed, it was handed to another
 * Worker, or nothing could take it for a day. `reason` is the one line the owner reads.
 */
export interface WaitingJobRecoveryRow {
  readonly org_id: string; readonly project_id: string; readonly task_id: string;
  readonly outcome: 'done_elsewhere' | 'reassigned' | 'expired'; readonly reason: string | null;
  readonly closed_at: string | null;
}

/**
 * #671: what the owner can do with one task, as `brunel.task_actions` works it out from the
 * task's own state. The browser reads `can_discard` and its note; it never recomputes them.
 */
export interface TaskActionRow {
  readonly org_id: string; readonly project_id: string; readonly task_id: string;
  readonly status: string; readonly can_discard: boolean; readonly note: string | null;
}

export interface RunningTask {
  readonly org_id: string; readonly project_id: string; readonly assignment_id: string; readonly task_id: string;
  readonly title: string; readonly project_name: string; readonly worker_number: number | null;
  readonly worker_name: string | null; readonly node_name: string; readonly started_at: string | null;
  readonly deadline_at: string | null;
  /**
   * The department role profile this run's task names. Absent when the database was read
   * before it had the column, null when the task names none.
   */
  readonly department_key?: string | null;
}
/** Where a run that is still going had got to, as its node last reported it. */
export interface RunProgress {
  readonly org_id: string; readonly project_id: string; readonly assignment_id: string;
  readonly seq: number; readonly tail: string; readonly observed_at: string;
}

/** One step of setting a computer up, as the node itself reported it. */
export interface ProvisioningEvent {
  readonly org_id: string; readonly node_id: string; readonly node_name: string; readonly operation_id: string;
  readonly seq: number; readonly state: string; readonly subject: string; readonly message: string;
  readonly detail: Readonly<Record<string, unknown>>; readonly observed_at: string;
}


/** Newest stated reading for one provider account pool (M45 / #276). */
export interface ProviderUsageLatest {
  readonly org_id: string; readonly provider_key: string; readonly account_ref: string;
  readonly pool_key: string; readonly node_id: string | null;
  readonly runtime_key: string | null; readonly model_key: string | null;
  readonly measure_unit: string; readonly used_value: number | null;
  readonly remaining_value: number | null; readonly limit_value: number | null;
  readonly resets_at: string | null; readonly source: string; readonly confidence: string;
  readonly observed_at: string;
}

/** Historical provider reading already recorded by M45. Read-only in the Inspector. */
export interface UsageMeasurementRow {
  readonly org_id: string; readonly id: string; readonly node_id: string | null;
  readonly project_id: string | null; readonly assignment_id: string | null;
  readonly provider_key: string; readonly account_ref: string; readonly pool_key: string;
  readonly runtime_key: string | null; readonly model_key: string | null;
  readonly measure_unit: string; readonly used_value: number | null;
  readonly remaining_value: number | null; readonly limit_value: number | null;
  readonly resets_at: string | null; readonly source: string; readonly confidence: string;
  readonly phase: 'before_run' | 'after_run' | 'standalone'; readonly observed_at: string;
}

/** Existing M45 per-run delta view: only rows with comparable before/after measurements exist. */
export interface RunPoolUsageRow {
  readonly org_id: string; readonly project_id: string; readonly assignment_id: string;
  readonly provider_key: string; readonly account_ref: string; readonly pool_key: string;
  readonly measure_unit: string; readonly used_before: number | null; readonly used_after: number | null;
  readonly used_delta: number | null; readonly remaining_before: number | null; readonly remaining_after: number | null;
  readonly remaining_delta: number | null; readonly confidence: string;
  readonly measured_before: string; readonly measured_after: string;
}

/** Last AI connection probe snapshot per node (#423). No secrets. */
export interface AiConnectionStatusRow {
  readonly org_id: string; readonly node_id: string; readonly connection_key: string;
  readonly installed: boolean; readonly auth_state: 'signed_in' | 'signed_out' | 'unknown';
  readonly capability_note: string | null;
  readonly meter_mode: 'percent' | 'spend' | 'unknown' | 'local_unlimited';
  readonly connection_mode?: 'commandos_vault' | 'node_bound' | 'local_no_auth' | 'explicit_api_key' | null;
  readonly last_probe_at: string; readonly last_error: string | null;
}

/** Effective Codex approval/sandbox state reported by one node (#436). */
export interface CodexAutonomyStatusRow {
  readonly org_id: string; readonly node_id: string; readonly unrestricted: boolean;
  readonly command_approvals: 'never_ask' | 'constrained' | 'unknown';
  readonly sandbox: 'workspace_write' | 'full_access' | 'constrained' | 'unknown';
  readonly network: 'enabled' | 'constrained' | 'unknown';
  readonly browser_approvals: 'saved' | 'still_asks' | 'managed' | 'unknown';
  readonly computer_use_approvals: 'saved' | 'still_asks' | 'os_permission_needed' | 'managed' | 'unknown';
  readonly plugin_approvals: 'always_allow' | 'constrained' | 'unknown';
  readonly effective_source: 'user_config' | 'managed_requirement' | 'desktop_setting' | 'unknown';
  readonly managed: boolean; readonly detail: string | null; readonly observed_at: string;
}

export interface AiLocalInstalledModelRow {
  readonly name: string;
  readonly fileSize: string | null;
}

export interface AiLocalLoadedModelRow {
  readonly name: string;
  readonly loadedSize: string | null;
  readonly processor: string | null;
  readonly contextLength: string | null;
}

/** Structured node-local runtime detail. No account quota or inferred GPU allocation. */
export interface AiLocalRuntimeStatusRow {
  readonly org_id: string; readonly node_id: string; readonly connection_key: string;
  readonly service_state: 'reachable' | 'unreachable' | 'unknown';
  readonly runtime_version: string | null;
  readonly installed_models_known: boolean;
  readonly installed_models: readonly AiLocalInstalledModelRow[];
  readonly loaded_models_known: boolean;
  readonly loaded_models: readonly AiLocalLoadedModelRow[];
  readonly last_successful_probe_at: string | null;
  readonly observed_at: string;
}

/** Owner AI usage soft/hard limits and warn thresholds (#276). */
export interface AiUsagePolicyRow {
  readonly org_id: string; readonly soft_limit_percent: number | null;
  readonly hard_limit_percent: number | null; readonly reserve_percent: number | null;
  readonly warn_thresholds: readonly number[] | null; readonly exhaustion_behavior: string;
  readonly never_substitute_pinned: boolean;
}

/** What a computer already answered for one Check again. `runtime_key` is the instruction's adapter key. */
export interface RuntimeRecheckInstruction {
  readonly org_id: string;
  readonly node_id: string;
  readonly runtime_key: string;
  readonly issued_at: string;
  readonly outcome: string | null;
  readonly detail: string | null;
}

/** A tool one computer has reported it cannot use, why, and when to try it again. */
export interface RuntimeOutage {
  readonly org_id: string; readonly node_id: string; readonly name: string; readonly runtime_key: string;
  readonly runtime_label: string | null; readonly reason_code: string; readonly observed_at: string;
  readonly available_from: string | null; readonly expired: boolean;
}

/** The newest sample one computer left behind: what it was under when it last reported. */
export interface NodePressureRow {
  readonly org_id: string; readonly node_id: string; readonly name: string; readonly worker_seats: number | null;
  readonly observed_at: string | null; readonly cpu_count: number | null; readonly cpu_load_percent: number | null;
  readonly memory_total_bytes: number | null; readonly memory_free_bytes: number | null; readonly agent_memory_bytes: number | null;
  readonly memory_free_percent: number | null; readonly graphics_count: number | null; readonly graphics_util_percent: number | null;
  readonly video_memory_total_bytes: number | null; readonly video_memory_used_bytes: number | null;
  readonly video_memory_free_percent: number | null; readonly active_runs: number | null;
  /**
   * The volume that holds this computer's run checkouts: free bytes, size, free share, and
   * whether that is under the disk guard (15 GB or 5%). Absent or null when the computer
   * has not said - an older node, or a database one migration behind.
   */
  readonly disk_free_bytes?: number | null; readonly disk_total_bytes?: number | null;
  readonly disk_free_percent?: number | null; readonly disk_low?: boolean | null;
}

/**
 * Whether one computer has room for another worker, and which reading says it has not.
 *
 * The four owner limits and the two sustained guards arrive on the same row (#483), so a
 * card and a detail never have to ask two places what one machine is allowed to do.
 */
export interface NodeCapacityRow {
  readonly org_id: string; readonly node_id: string; readonly name: string; readonly worker_seats: number | null;
  readonly reserve_memory_percent: number | null; readonly reserve_graphics_percent: number | null;
  readonly observed_at: string | null; readonly memory_free_percent: number | null;
  readonly video_memory_free_percent: number | null; readonly active_runs: number | null;
  readonly accepting: boolean | null; readonly holding_back: string | null;
  /** Null is a ceiling that is off, never a ceiling of zero. */
  readonly cpu_ceiling_percent: number | null; readonly graphics_ceiling_percent: number | null;
  readonly cpu_sustained_percent: number | null; readonly cpu_sample_count: number | null;
  readonly cpu_guard_engaged: boolean | null; readonly cpu_guard_changed_at: string | null;
  readonly graphics_sustained_percent: number | null; readonly graphics_sample_count: number | null;
  readonly graphics_guard_engaged: boolean | null; readonly graphics_guard_changed_at: string | null;
}

/** The numbers a sustained load guard is made of, as the database states them. */
export interface CapacityGuardContractRow {
  readonly window_seconds: number; readonly min_samples: number;
  readonly release_margin_percent: number; readonly cooldown_seconds: number;
}

/**
 * One reading a computer left behind, for the small recent history a machine's detail
 * draws. Bounded on the server side by the day of retention M17 already applies, and
 * bounded again here by the newest few hundred rows.
 */
export interface NodeResourceSampleRow {
  readonly org_id: string; readonly node_id: string; readonly observed_at: string;
  readonly cpu_load_percent: number | null;
  readonly memory_total_bytes: number | null; readonly memory_free_bytes: number | null;
  readonly graphics_util_percent: number | null;
  readonly video_memory_total_bytes: number | null; readonly video_memory_used_bytes: number | null;
}

export interface RunSummary { readonly org_id: string; readonly project_id: string; readonly assignment_id: string; readonly success: boolean; readonly summary: string; readonly output_tail: string | null; readonly reported_at: string }

/**
 * A finished run with the run itself attached: which worker held it, when it
 * began beside when it reported, what it was assigned to run with, and what
 * actually ran it. A run whose dispatch record is gone still appears, with no
 * worker and no start; a run that reported no tool leaves those null rather
 * than repeating the assigned one back.
 */
export interface FinishedRun extends RunSummary {
  readonly task_id: string;
  readonly worker_id: string | null;
  readonly worker_number: number | null;
  readonly started_at: string | null;
  readonly assigned_offering_key: string | null;
  readonly assigned_runtime_key: string | null;
  readonly assigned_model_key: string | null;
  readonly runtime_key: string | null;
  readonly model_key: string | null;
  readonly files_changed?: unknown;
}

export type ReadResult<T> = { readonly ok: true; readonly rows: readonly T[] } | { readonly ok: false; readonly message: string };

const LIMIT = 50;
/** The audit columns both audit reads ask for, so the two cannot drift apart. */
const AUDIT_COLUMNS = 'org_id,id,event_type,reason_code,deciding_gate,occurred_at,actor_id,payload';
/**
 * How far back the routine machine chatter is read. It is kept deeper than the
 * ordinary cap because it is what the owner is shown when they ask to see
 * everything, and because the count of what is being held back is taken from
 * these rows - a shallow read would understate it.
 */
const CHATTER_LIMIT = 200;
/**
 * How many resource readings the desk holds for the machine history. Two computers
 * reporting every thirty seconds is about an hour each, which is what a card-sized
 * history needs; the server keeps a day and prunes itself.
 */
const SAMPLE_LIMIT = 240;
/** The plan columns both plan reads ask for, so the two cannot drift apart. */
const PLAN_COLUMNS = 'org_id,project_id,id,command_id,status,summary,tasks,reason,created_at,mission_id,authority,held_for,held_detail';
/**
 * How many task rows are held. A plan is at most twelve tasks, so this covers
 * the recent missions the transcript is actually showing; anything older has
 * scrolled out of the conversation long before.
 */
const TASK_LIMIT = 200;
/** #671: only what the Work Board discard control needs from `brunel.task_actions`. */
const TASK_ACTION_COLUMNS = 'org_id,project_id,task_id,status,can_discard,note';
/** #676: the columns one History page hydrates, the same ones the ordinary readers select. */
const MISSION_TASK_COLUMNS = 'org_id,project_id,id,mission_id,title,status';
const FINISHED_RUN_COLUMNS = 'org_id,project_id,assignment_id,task_id,worker_id,worker_number,started_at,reported_at,success,summary,output_tail,assigned_offering_key,assigned_runtime_key,assigned_model_key,runtime_key,model_key,files_changed';
const ROADMAP_COLUMNS = 'org_id,id,title,summary,horizon,status,rank,project_id,owner_label,source_command_id,created_at,updated_at';
const COMMAND_PLAN_COLUMNS = 'org_id,id,goal,status,verdict,planner_key,instruction,success_criteria,stop_conditions,expected_observation,requires_approval,observation,result_summary,proposed_next_instruction,failure,previous_plan_id,created_at,drafted_at,sent_at,evaluated_at,worker_id,worker_number,worker_name,worker_status,controller_id,controller_name,session_id,terminal_status,node_id,node_name';
const WORK_BOARD_HISTORY_COLUMNS = 'org_id,identity_key,sort_at,source_kind,project_id,task_id,assignment_id,command_plan_id,roadmap_item_id';

/** #676: one finished Work Board identity, in the order History pages it. */
export interface WorkBoardHistoryRow {
  readonly org_id: string;
  readonly identity_key: string;
  readonly sort_at: string;
  readonly source_kind: 'task' | 'finished' | 'command_plan' | 'roadmap';
  readonly project_id: string | null;
  readonly task_id: string | null;
  readonly assignment_id: string | null;
  readonly command_plan_id: string | null;
  readonly roadmap_item_id: string | null;
}

/** #676: one History page of identities, with the exact total when the Data API gave one. */
export type WorkBoardHistoryPageRead =
  | { readonly ok: true; readonly rows: readonly WorkBoardHistoryRow[]; readonly total: number | null }
  | { readonly ok: false; readonly message: string };

/** #676: one row of a project-scoped table, named exactly: its project and its own id. */
export interface ProjectKey { readonly projectId: string; readonly id: string }
/**
 * Observer history is append-only and can outgrow a browser read. Fetch the
 * newest bounded window, then restore chronological order for timeline/archive
 * consumers before exposing it.
 */
const OBSERVER_MESSAGE_LIMIT = 500;

async function read<T>(query: PromiseLike<{ data: unknown; error: { message: string } | null }>): Promise<ReadResult<T>> {
  const { data, error } = await query;
  if (error) return { ok: false, message: 'Could not load data.' };
  return { ok: true, rows: (Array.isArray(data) ? data : []) as T[] };
}

/**
 * #676: read rows named by exact (project_id, id) pairs: one query per project, and only rows
 * whose own pair was asked for are kept, so a same id in another project can never come back.
 */
async function readByProjectKeys<T>(
  keys: readonly ProjectKey[],
  query: (projectId: string, ids: string[]) => PromiseLike<{ data: unknown; error: { message: string } | null }>,
  keyOf: (row: T) => readonly [string, string],
): Promise<ReadResult<T>> {
  const byProject = new Map<string, Set<string>>();
  for (const key of keys) byProject.set(key.projectId, (byProject.get(key.projectId) ?? new Set()).add(key.id));
  const reads = await Promise.all([...byProject].map(([projectId, ids]) => read<T>(query(projectId, [...ids]))));
  const wanted = new Set(keys.map((key) => `${key.projectId}:${key.id}`));
  const rows: T[] = [];
  for (const result of reads) {
    if (!result.ok) return result;
    rows.push(...result.rows.filter((row) => wanted.has(keyOf(row).join(':'))));
  }
  return { ok: true, rows };
}

async function readNewestChronologically<T>(
  query: PromiseLike<{ data: unknown; error: { message: string } | null }>,
): Promise<ReadResult<T>> {
  const result = await read<T>(query);
  if (!result.ok) return result;
  return { ok: true, rows: [...result.rows].reverse() };
}

/** One row of the shared owner roadmap (Inspector + Main Command). */
export interface RoadmapItemRow {
  readonly org_id: string;
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  readonly horizon: 'current' | 'next' | 'later';
  readonly status: 'open' | 'in_progress' | 'blocked' | 'done' | 'dropped';
  readonly rank: number;
  readonly project_id: string | null;
  readonly owner_label: string | null;
  readonly source_command_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

const OWNER_COMMAND_COLUMNS = 'org_id,id,seq,content,project_id,context_project_id,reply_targets,observer_thread_id,request_id,created_at';

export function createReader(client: SupabaseClient) {
  const db = client.schema('brunel');
  /** Remember a pre-migration view for this page lifetime without doubling every Worker poll. */
  let terminalOriginAvailable: boolean | null = null;
  return {
    organizations: () => read<Organization>(db.from('organizations').select('id,name').order('name')),
    projects: (orgId: string) => read<Project>(db.from('projects').select('org_id,id,name').eq('org_id', orgId).order('name')),
    repositories: (orgId: string) => read<Repository>(
      db.from('repositories').select('org_id,project_id,id,name,default_ref').eq('org_id', orgId).order('name')),
    workers: (orgId: string) => read<Worker>(db.from('workers').select('org_id,id,number,name,status,retired_at').eq('org_id', orgId).order('number')),
    missions: (orgId: string) => read<Mission>(db.from('missions').select('org_id,project_id,id,title,status').eq('org_id', orgId).limit(LIMIT)),
    ownerCommands: (orgId: string) => read<OwnerCommand>(db.from('owner_commands').select('org_id,id,seq,content,project_id,context_project_id,reply_targets,observer_thread_id,request_id,created_at').eq('org_id', orgId).order('seq', { ascending: false }).limit(LIMIT)),
    // Plans can outlive the newest command window. Fetch those command texts by id
    // so an old proposal can be matched to the work that finished or replaced it.
    ownerCommandsByIds: (orgId: string, ids: readonly string[]): Promise<ReadResult<OwnerCommand>> => {
      const unique = [...new Set(ids.filter((id) => id !== ''))];
      if (unique.length === 0) return Promise.resolve({ ok: true, rows: [] });
      return read<OwnerCommand>(db.from('owner_commands').select(OWNER_COMMAND_COLUMNS).eq('org_id', orgId).in('id', unique));
    },
    // Newest first: the bounded window must hold the recipients of the newest owner
    // commands read above, not the org's oldest 500 rows. Consumers match by command id.
    ownerCommandControls: (orgId: string) => read<OwnerCommandControl>(
      db.from('owner_command_controls')
        .select('org_id,command_id,action,state,reason,summary,worker_number,created_at')
        .eq('org_id', orgId).order('created_at', { ascending: false }).limit(500)),
    // #644 Slice D: the one workforce snapshot (read-only RPC). Null when it could not be read.
    workforceSnapshot: async (orgId: string): Promise<WorkforceSnapshot | null> => {
      const { data, error } = await client.rpc('workforce_snapshot', { request: { orgId } });
      const result = data as { ok?: boolean; value?: WorkforceSnapshot } | null;
      return error || result?.ok !== true || result.value === undefined ? null : result.value;
    },
    ownerCommandWorkerRecipients: (orgId: string) => read<OwnerCommandWorkerRecipient>(
      db.from('owner_command_worker_recipients')
        .select('org_id,owner_command_id,recipient_kind,worker_id,created_at')
        .eq('org_id', orgId).order('created_at', { ascending: false }).limit(500)),
    // The events about work and decisions, asked for by name. The trail is
    // nearly all machine chatter, so the newest fifty rows of it are all
    // chatter: filtering after the read would leave the panel with nothing to
    // show. Asking the database for the types that matter is what makes the
    // panel useful, and it costs a longer query and one read more.
    auditEvents: (orgId: string) => read<AuditEvent>(db.from('audit_events').select(AUDIT_COLUMNS).eq('org_id', orgId).in('event_type', [...MEANINGFUL_EVENT_TYPES]).order('occurred_at', { ascending: false }).limit(LIMIT)),
    // The chatter itself, for when the owner asks to see everything.
    routineAuditEvents: (orgId: string) => read<AuditEvent>(db.from('audit_events').select(AUDIT_COLUMNS).eq('org_id', orgId).in('event_type', [...ROUTINE_EVENT_TYPES]).order('occurred_at', { ascending: false }).limit(CHATTER_LIMIT)),
    reports: (orgId: string) => read<ReportMessage>(db.from('report_messages').select('org_id,id,stream_id,seq,envelope,created_at').eq('org_id', orgId).order('created_at', { ascending: false }).limit(LIMIT)),
    chatStreams: (orgId: string) => read<ChatStream>(db.from('streams').select('org_id,project_id,id,session_id').eq('org_id', orgId).eq('kind', 'chat')),
    chatMessages: (orgId: string, streamId: string) => read<ChatMessage>(
      db.from('chat_messages')
        .select('org_id,id,stream_id,seq,content,created_at,author_kind,worker_id,owner_command_id,author_identity')
        .eq('org_id', orgId).eq('stream_id', streamId).order('seq').limit(200)),
    workerChatMessages: (orgId: string) => read<ChatMessage>(
      db.from('chat_messages')
        .select('org_id,id,stream_id,seq,content,created_at,author_kind,worker_id,owner_command_id,author_identity')
        .eq('org_id', orgId).eq('author_kind', 'worker').not('owner_command_id', 'is', null)
        // Newest first, so a new Worker reply is never cut off by the window once the org
        // has more than 500. The timeline places each reply by its owner turn, not row order.
        .order('created_at', { ascending: false }).limit(500)),
    sessions: (orgId: string) => read<SessionRef>(db.from('sessions').select('org_id,id,worker_id,project_id,node_id,status').eq('org_id', orgId).limit(200)),
    workforceSessions: (orgId: string) => read<WorkforceSession>(db.from('sessions').select('org_id,id,worker_id,project_id,node_id,status,lifecycle,offering_key').eq('org_id', orgId).limit(500)),
    nodeOfferings: (orgId: string) => read<NodeOffering>(db.from('node_offerings').select('org_id,node_id,offering_key').eq('org_id', orgId).limit(500)),
    modelOfferings: (orgId: string) => read<ModelOfferingRef>(db.from('model_offerings').select('org_id,key,runtime_key,model_key,execution_location,provider_label').eq('org_id', orgId).limit(500)),
    workerOfferingPreferences: (orgId: string) => read<WorkerOfferingPreference>(db.from('worker_offering_preferences').select('org_id,worker_id,node_id,offering_key,updated_at').eq('org_id', orgId).limit(200)),
    workerAiExecutionProfiles: (orgId: string) => read<WorkerAiExecutionProfileRow>(db.from('worker_ai_execution_profiles').select('org_id,worker_id,node_id,session_id,mode,configured_offering_key,configured_runtime_key,configured_model_key,configured_effort_key,configured_at,actual_runtime_key,actual_model_key,actual_effort_key,actual_reported_at,busy').eq('org_id', orgId).limit(200)),
    aiRuntimeCapabilities: (orgId: string) => read<AiRuntimeCapabilityRow>(db.from('ai_runtime_capabilities').select('org_id,node_id,runtime_key,efforts,default_effort,live_model_switch,live_effort_switch,evidence,reported_at').eq('org_id', orgId).limit(200)),
    runtimeProofs: (orgId: string) => read<RuntimeProof>(db.from('runtime_proof').select('org_id,node_id,node_name,runtime_key,runtime_label,offering_count,run_count,success_count,failure_count,proven,last_run_at,last_success_at,last_failure_at,last_success_summary,last_failure_summary').eq('org_id', orgId).order('node_name').order('runtime_key')),
    commandPlannerHostStatus: (orgId: string) => read<CommandPlannerHostStatus>(
      db.from('command_planner_host_status')
        .select('org_id,node_id,node_name,runtime_key,fallback_mode,set_at,last_seen_at,connected,offered,runtime_available,configured_offering_key,configured_model_key,configured_effort_key,configured_at,actual_runtime_key,actual_model_key,actual_effort_key,actual_reported_at')
        .eq('org_id', orgId).limit(1)),
    // One row per Worker, with the terminal it owns or nulls where it has none.
    // Every owner goal and what became of it, newest first.
    commandPlans: (orgId: string, limit = 12) => read<CommandPlanRow>(
      db.from('command_plan_state').select('org_id,id,goal,status,verdict,planner_key,instruction,success_criteria,stop_conditions,expected_observation,requires_approval,observation,result_summary,proposed_next_instruction,failure,previous_plan_id,created_at,drafted_at,sent_at,evaluated_at,worker_id,worker_number,worker_name,worker_status,controller_id,controller_name,session_id,terminal_status,node_id,node_name')
        .eq('org_id', orgId).order('created_at', { ascending: false }).limit(limit)),
    // #879: collapsed to exactly one row per Worker here, so every screen counts Workers
    // rather than joined rows, even while an older hosted view still multiplies them.
    workerTerminals: async (orgId: string): Promise<ReadResult<WorkerTerminalRow>> => {
      const columns = 'org_id,worker_id,worker_number,worker_name,worker_status,session_id,terminal_status,pid,shell,cwd,opened_at,last_activity_at,closed_at,exit_code,failure,input_holder,input_held,input_holder_controller,input_holder_controller_name,node_id,node_name,workforce_session_id,offering_key,project_id,lifecycle,session_status,project_name,task_id,task_title,task_started_at,task_deadline_at';
      const loadTerminals = async (): Promise<ReadResult<WorkerTerminalRow>> => {
        if (terminalOriginAvailable === false) {
          const old = await read<WorkerTerminalRow>(db.from('worker_terminals').select(columns)
            .eq('org_id', orgId).order('worker_number'));
          return old.ok ? { ok: true, rows: old.rows.map((row) => ({ ...row, terminal_origin: null })) } : old;
        }
        const { data, error } = await db.from('worker_terminals').select(`${columns},terminal_origin`)
          .eq('org_id', orgId).order('worker_number');
        if (error === null) {
          terminalOriginAvailable = true;
          return { ok: true, rows: (Array.isArray(data) ? data : []) as WorkerTerminalRow[] };
        }
        if (error.code !== '42703' || !error.message.includes('terminal_origin')) {
          return { ok: false, message: 'Could not load data.' };
        }
        terminalOriginAvailable = false;
        const old = await read<WorkerTerminalRow>(db.from('worker_terminals').select(columns)
          .eq('org_id', orgId).order('worker_number'));
        return old.ok ? { ok: true, rows: old.rows.map((row) => ({ ...row, terminal_origin: null })) } : old;
      };
      const [terminals, running] = await Promise.all([
        loadTerminals(),
        read<RunningTaskProject>(db.from('running_tasks').select('task_id,project_id,project_name').eq('org_id', orgId).limit(LIMIT)),
      ]);
      if (!terminals.ok) return terminals;
      return { ok: true, rows: [...canonicalWorkerTerminalRows(terminals.rows, running.ok ? running.rows : [])] };
    },
    // What was done to one terminal and what was decided - metadata, never what was typed.
    terminalActions: (orgId: string, sessionId: string, limit = 30) => read<TerminalActionRow>(
      db.from('terminal_actions').select('org_id,id,session_id,at,action,source,decision,reason,byte_count,control_bytes,controller_id')
        .eq('org_id', orgId).eq('session_id', sessionId).order('id', { ascending: false }).limit(limit)),
    terminalSessions: (orgId: string, nodeId: string) => read<TerminalSessionRow>(
      db.from('live_terminals')
        .select('org_id,id,node_id,node_name,label,shell,cwd,status,pid,columns_wanted,rows_wanted,requested_at,opened_at,last_activity_at,close_requested_at,closed_at,exit_code,failure,live,waiting_to_start')
        .eq('org_id', orgId).eq('node_id', nodeId).order('requested_at', { ascending: false }).limit(100)),
    // #805: the newest chunks of a session, newest first; a viewer attaches here instead of at seq 1.
    terminalOutputTail: (orgId: string, sessionId: string, limit = 1000) => read<TerminalOutputRow>(
      db.from('terminal_output').select('org_id,session_id,seq,at,chunk')
        .eq('org_id', orgId).eq('session_id', sessionId).order('seq', { ascending: false }).limit(limit)),
    terminalOutput: (orgId: string, sessionId: string, afterSeq = 0) => read<TerminalOutputRow>(
      db.from('terminal_output').select('org_id,session_id,seq,at,chunk')
        .eq('org_id', orgId).eq('session_id', sessionId).gt('seq', afterSeq).order('seq').limit(500)),
    // How much of fromSeq..toSeq is stored, for the Core watcher's check of a hole in what Realtime
    // delivered. Sequence keys only, never the output; null when storage could not be read.
    terminalOutputStored: async (orgId: string, sessionId: string, fromSeq: number, toSeq: number): Promise<StoredTerminalSequences | null> => {
      const { data, error, count } = await db.from('terminal_output')
        .select('seq', { count: 'exact' })
        .eq('org_id', orgId).eq('session_id', sessionId).gte('seq', fromSeq).lte('seq', toSeq)
        .order('seq', { ascending: false }).limit(1);
      if (error || typeof count !== 'number') return null;
      const newest = Array.isArray(data) ? (data[0] as { seq?: unknown } | undefined)?.seq : null;
      return { stored: count, newest: typeof newest === 'number' ? newest : null };
    },
    plans: (orgId: string) => read<Plan>(db.from('plans').select(PLAN_COLUMNS).eq('org_id', orgId).in('status', ['proposed', 'failed']).order('created_at', { ascending: false }).limit(LIMIT)),
    // The standing routing instruction, one row per project while one is set. No row is Auto.
    workerRouting: (orgId: string) => read<WorkerRoutingRow>(db.from('worker_routing').select('org_id,project_id,project_name,worker_number,worker_name,node_name,set_at').eq('org_id', orgId).limit(100)),
    observerThreads: (orgId: string) => read<ObserverThreadRow>(
      db.from('observer_threads').select('org_id,id,batch_key,suggestion_id,title,status,unread_count,roadmap_item_id,last_decision,reply_requested_at,reply_pending_roles,reply_command_id,attention_snoozed_until,created_at,updated_at').eq('org_id', orgId).eq('context_kind', 'finding').order('updated_at', { ascending: false }).limit(100),
    ),
    observerMessages: (orgId: string) => readNewestChronologically<ObserverMessageRow>(
      db.from('observer_messages').select('org_id,id,thread_id,author_role,author_label,body,kind,suggestion_id,batch_key,evidence,warnings,alternatives,resource_profile,resource_snapshot,execution_identity,discussion_id,conclusion,decision,created_at').eq('org_id', orgId).order('created_at', { ascending: false }).limit(OBSERVER_MESSAGE_LIMIT),
    ),
    // Every plan, whatever was decided about it, so the owner can see where one came from.
    planHistory: (orgId: string) => read<Plan>(db.from('plans').select(PLAN_COLUMNS).eq('org_id', orgId).order('created_at', { ascending: false }).limit(LIMIT)),
    // The tasks an approved plan became. Read so the transcript can say what came
    // of a plan without guessing which runs belonged to it.
    missionTasks: (orgId: string) => read<MissionTask>(db.from('tasks').select('org_id,project_id,id,mission_id,title,status').eq('org_id', orgId).order('created_at', { ascending: false }).limit(TASK_LIMIT)),
    // #671: what can be done with each task, newest first like the tasks the board is showing.
    // Only jobs whose wait has ended. A database without the table answers an error, which
    // every caller reads as "none".
    waitingJobRecoveries: (orgId: string) => read<WaitingJobRecoveryRow>(db.from('waiting_job_recoveries').select('org_id,project_id,task_id,outcome,reason,closed_at').eq('org_id', orgId).not('outcome', 'is', null).order('closed_at', { ascending: false }).limit(TASK_LIMIT)),
    taskActions: (orgId: string) => read<TaskActionRow>(db.from('task_actions').select(TASK_ACTION_COLUMNS).eq('org_id', orgId).order('created_at', { ascending: false }).limit(TASK_LIMIT)),
    // Durable merge evidence for an assignment's pull request: GitHub reported it merged when the
    // run recorded it, or the completion loop merged it / observed it merged afterwards.
    mergedPullRequests: (orgId: string) => read<{ readonly assignment_id: string }>(db.from('completion_pull_requests').select('assignment_id').eq('org_id', orgId).not('merged_at', 'is', null).order('recorded_at', { ascending: false }).limit(TASK_LIMIT)),
    mergeCompletionActions: (orgId: string) => read<{ readonly assignment_id: string; readonly stage: string; readonly outcome: string }>(db.from('worker_completion_actions').select('assignment_id,stage,outcome').eq('org_id', orgId).in('stage', ['merge', 'integration']).order('recorded_at', { ascending: false }).limit(TASK_LIMIT)),
    // #671: one exact task's action row, re-read after a discard request whatever its answer was.
    taskAction: (orgId: string, projectId: string, taskId: string) => read<TaskActionRow>(db.from('task_actions').select(TASK_ACTION_COLUMNS).eq('org_id', orgId).eq('project_id', projectId).eq('task_id', taskId).limit(1)),
    // #676 Work Board History: one range page of finished identities, newest first, with an exact count.
    workBoardHistoryPage: async (orgId: string, from: number, to: number): Promise<WorkBoardHistoryPageRead> => {
      const { data, error, count } = await db.from('work_board_history_index')
        .select(WORK_BOARD_HISTORY_COLUMNS, { count: 'exact' })
        .eq('org_id', orgId)
        .order('sort_at', { ascending: false })
        .order('identity_key', { ascending: false })
        .range(from, to);
      if (error) return { ok: false, message: 'Could not load data.' };
      return { ok: true, rows: (Array.isArray(data) ? data : []) as WorkBoardHistoryRow[], total: typeof count === 'number' ? count : null };
    },
    // #676: only the rows one History page names. Tasks, runs and task actions are keyed
    // (org_id, project_id, id), so each of those reads is per project.
    tasksByKeys: (orgId: string, keys: readonly ProjectKey[]) => readByProjectKeys<MissionTask>(keys, (projectId, ids) =>
      db.from('tasks').select(MISSION_TASK_COLUMNS).eq('org_id', orgId).eq('project_id', projectId).in('id', ids), (row) => [row.project_id, row.id]),
    finishedRunsByKeys: (orgId: string, keys: readonly ProjectKey[]) => readByProjectKeys<FinishedRun>(keys, (projectId, ids) =>
      db.from('finished_runs').select(FINISHED_RUN_COLUMNS).eq('org_id', orgId).eq('project_id', projectId).in('assignment_id', ids), (row) => [row.project_id, row.assignment_id]),
    commandPlansByIds: (orgId: string, ids: readonly string[]) => read<CommandPlanRow>(db.from('command_plan_state').select(COMMAND_PLAN_COLUMNS).eq('org_id', orgId).in('id', [...ids])),
    roadmapItemsByIds: (orgId: string, ids: readonly string[]) => read<RoadmapItemRow>(db.from('roadmap_items').select(ROADMAP_COLUMNS).eq('org_id', orgId).in('id', [...ids])),
    taskActionsByKeys: (orgId: string, keys: readonly ProjectKey[]) => readByProjectKeys<TaskActionRow>(keys, (projectId, ids) =>
      db.from('task_actions').select(TASK_ACTION_COLUMNS).eq('org_id', orgId).eq('project_id', projectId).in('task_id', ids), (row) => [row.project_id, row.task_id]),
    runSummaries: (orgId: string) => read<FinishedRun>(db.from('finished_runs').select('org_id,project_id,assignment_id,task_id,worker_id,worker_number,started_at,reported_at,success,summary,output_tail,assigned_offering_key,assigned_runtime_key,assigned_model_key,runtime_key,model_key,files_changed').eq('org_id', orgId).order('reported_at', { ascending: false }).limit(LIMIT)),
    runningProgress: (orgId: string) => read<RunProgress>(db.from('running_progress').select('org_id,project_id,assignment_id,seq,tail,observed_at').eq('org_id', orgId).limit(LIMIT)),
    // The department is asked for with the rest. A database from before departments refuses
    // the column, and the same rows are then read without it: a run in progress is never
    // hidden for want of a label.
    runningTasks: async (orgId: string) => {
      const columns = 'org_id,project_id,assignment_id,task_id,title,project_name,worker_number,worker_name,node_name,started_at,deadline_at';
      const rows = (select: string) => read<RunningTask>(db.from('running_tasks').select(select).eq('org_id', orgId).order('started_at', { ascending: true }).limit(LIMIT));
      const withDepartment = await rows(`${columns},department_key`);
      return withDepartment.ok ? withDepartment : rows(columns);
    },
    nodePresence: (orgId: string) => read<NodePresence>(db.from('node_presence').select('org_id,node_id,name,status,last_seen_at').eq('org_id', orgId).order('name')),
    nodeVersions: (orgId: string) => read<NodeVersion>(db.from('node_versions').select('org_id,node_id,name,running_version,target_version,behind,update_state,update_version,update_detail,update_reported_at,last_seen_at').eq('org_id', orgId).order('name')),
    nodeReleases: (orgId: string) => read<NodeRelease>(db.from('node_releases').select('org_id,version,bundle_url,bundle_sha256,notes,published_at').eq('org_id', orgId).order('published_at', { ascending: false }).limit(LIMIT)),
    nodeUpdateSettings: (orgId: string) => read<NodeUpdateSettingsRow>(db.from('node_update_settings').select('org_id,automatic_when_idle').eq('org_id', orgId).limit(1)),
    launchCatalog: (orgId: string) => read<LaunchCatalogRow>(db.from('launch_catalog').select('org_id,runtime_key,runtime_label,position,file,args,env,needs_model,updated_at,updated_by').eq('org_id', orgId).order('position').order('runtime_key')),
    runtimeOutages: (orgId: string) => read<RuntimeOutage>(db.from('runtime_outages').select('org_id,node_id,name,runtime_key,runtime_label,reason_code,observed_at,available_from,expired').eq('org_id', orgId).order('name').limit(LIMIT)),
    // Check again refusals already stored on the computer's instruction row. Display only.
    runtimeRecheckInstructions: async (orgId: string): Promise<ReadResult<RuntimeRecheckInstruction>> => {
      const page = await read<{
        org_id: string; node_id: string; adapter_key: string | null;
        issued_at: string; outcome: string | null; detail: string | null;
      }>(db.from('node_instructions')
        .select('org_id,node_id,adapter_key,issued_at,outcome,detail')
        .eq('org_id', orgId)
        .eq('instruction', 'recheck_runtime')
        .order('issued_at', { ascending: false })
        .limit(100));
      if (!page.ok) return page;
      return {
        ok: true,
        rows: page.rows.flatMap((row) => (
          row.adapter_key === null || row.adapter_key.trim() === '' ? [] : [{
            org_id: row.org_id,
            node_id: row.node_id,
            runtime_key: row.adapter_key,
            issued_at: row.issued_at,
            outcome: row.outcome,
            detail: row.detail,
          }]
        )),
      };
    },
    providerUsageLatest: (orgId: string) => read<ProviderUsageLatest>(db.from('provider_usage_latest').select('org_id,provider_key,account_ref,pool_key,node_id,runtime_key,model_key,measure_unit,used_value,remaining_value,limit_value,resets_at,source,confidence,observed_at').eq('org_id', orgId).limit(LIMIT)),
    usageMeasurements: (orgId: string) => read<UsageMeasurementRow>(db.from('usage_measurements').select('org_id,id,node_id,project_id,assignment_id,provider_key,account_ref,pool_key,runtime_key,model_key,measure_unit,used_value,remaining_value,limit_value,resets_at,source,confidence,phase,observed_at').eq('org_id', orgId).order('observed_at', { ascending: false }).limit(200)),
    runPoolUsage: (orgId: string) => read<RunPoolUsageRow>(db.from('run_pool_usage').select('org_id,project_id,assignment_id,provider_key,account_ref,pool_key,measure_unit,used_before,used_after,used_delta,remaining_before,remaining_after,remaining_delta,confidence,measured_before,measured_after').eq('org_id', orgId).order('measured_after', { ascending: false }).limit(100)),
    aiUsagePolicies: (orgId: string) => read<AiUsagePolicyRow>(db.from('ai_usage_policies').select('org_id,soft_limit_percent,hard_limit_percent,reserve_percent,warn_thresholds,exhaustion_behavior,never_substitute_pinned').eq('org_id', orgId).limit(1)),
    aiConnectionStatuses: (orgId: string) => read<AiConnectionStatusRow>(db.from('ai_connection_status').select('org_id,node_id,connection_key,installed,auth_state,capability_note,meter_mode,connection_mode,last_probe_at,last_error').eq('org_id', orgId).limit(200)),
    aiConnectionRoleBindings: (orgId: string) => read<AiConnectionRoleBindingRow>(db.from('ai_connection_role_bindings').select('org_id,consumer_role,connection_key,node_id,account_ref,external_session_id,updated_at').eq('org_id', orgId).limit(4)),
    aiLocalRuntimeStatuses: (orgId: string) => read<AiLocalRuntimeStatusRow>(db.from('ai_local_runtime_status').select('org_id,node_id,connection_key,service_state,runtime_version,installed_models_known,installed_models,loaded_models_known,loaded_models,last_successful_probe_at,observed_at').eq('org_id', orgId).limit(200)),
    codexAutonomyStatuses: (orgId: string) => read<CodexAutonomyStatusRow>(db.from('codex_autonomy_status').select('org_id,node_id,unrestricted,command_approvals,sandbox,network,browser_approvals,computer_use_approvals,plugin_approvals,effective_source,managed,detail,observed_at').eq('org_id', orgId).limit(200)),
    nodePressure: async (orgId: string) => {
      const columns = 'org_id,node_id,name,worker_seats,observed_at,cpu_count,cpu_load_percent,memory_total_bytes,memory_free_bytes,agent_memory_bytes,memory_free_percent,graphics_count,graphics_util_percent,video_memory_total_bytes,video_memory_used_bytes,video_memory_free_percent,active_runs';
      const withDisk = await read<NodePressureRow>(db.from('node_pressure').select(`${columns},disk_free_bytes,disk_total_bytes,disk_free_percent,disk_low`).eq('org_id', orgId).order('name').limit(LIMIT));
      // A database one migration behind has no disk columns yet. The readings it does have
      // still load, rather than the whole pressure read failing for a missing column.
      return withDisk.ok ? withDisk : read<NodePressureRow>(db.from('node_pressure').select(columns).eq('org_id', orgId).order('name').limit(LIMIT));
    },
    nodeCapacity: (orgId: string) => read<NodeCapacityRow>(db.from('node_capacity').select('org_id,node_id,name,worker_seats,reserve_memory_percent,reserve_graphics_percent,observed_at,memory_free_percent,video_memory_free_percent,active_runs,accepting,holding_back,cpu_ceiling_percent,graphics_ceiling_percent,cpu_sustained_percent,cpu_sample_count,cpu_guard_engaged,cpu_guard_changed_at,graphics_sustained_percent,graphics_sample_count,graphics_guard_engaged,graphics_guard_changed_at').eq('org_id', orgId).order('name').limit(LIMIT)),
    capacityGuardContract: () => read<CapacityGuardContractRow>(db.from('capacity_guard_contract').select('window_seconds,min_samples,release_margin_percent,cooldown_seconds').limit(1)),
    nodeResourceSamples: (orgId: string) => read<NodeResourceSampleRow>(db.from('node_resources').select('org_id,node_id,observed_at,cpu_load_percent,memory_total_bytes,memory_free_bytes,graphics_util_percent,video_memory_total_bytes,video_memory_used_bytes').eq('org_id', orgId).order('observed_at', { ascending: false }).limit(SAMPLE_LIMIT)),
    /** The permanent owner rules Rules shows. The same for every organization; read-only. */
    ownerRules: () => read<OwnerRuleRow>(db.from('owner_rules').select('rule_key,statement,permanent,stated_on,source').order('created_at').limit(LIMIT)),
    approvalPolicies: (orgId: string) => read<ApprovalPolicyRow>(db.from('approval_policies').select('org_id,mode,plan_approval,updated_at').eq('org_id', orgId).limit(1)),
    // #743: the exact earlier message each owner message replies to. Read on its own, so a
    // database without the column only loses the quotes, never the conversation.
    ownerCommandReplyRefs: (orgId: string) => read<{ readonly id: string; readonly reply_to: unknown }>(db.from('owner_commands').select('id,reply_to').eq('org_id', orgId).not('reply_to', 'is', null).order('seq', { ascending: false }).limit(LIMIT)),
    // The Overseer heartbeat (15-minute Autopilot rounds): the switch, last/next times, newest round.
    autopilotHeartbeat: (orgId: string) => read<AutopilotHeartbeatRow>(db.from('autopilot_heartbeats').select('paused,next_round_at,last_started_at,last_finished_at,last_outcome').eq('org_id', orgId).limit(1)),
    autopilotLatestRound: (orgId: string) => read<AutopilotRoundRow>(db.from('autopilot_rounds').select('id,state,slot_at,started_at,finished_at,outcome').eq('org_id', orgId).order('created_at', { ascending: false }).limit(1)),
    roadmapItems: async (orgId: string): Promise<ReadResult<RoadmapItemRow>> => {
      const pageSize = 500;
      const rows: RoadmapItemRow[] = [];
      for (let start = 0; ; start += pageSize) {
        const page = await read<RoadmapItemRow>(db.from('roadmap_items')
          .select('org_id,id,title,summary,horizon,status,rank,project_id,owner_label,source_command_id,created_at,updated_at')
          .eq('org_id', orgId).order('horizon').order('rank').order('created_at').order('id')
          .range(start, start + pageSize - 1));
        if (!page.ok) return page;
        rows.push(...page.rows);
        if (page.rows.length < pageSize) return { ok: true, rows };
      }
    },
    nodeIdeAdapters: (orgId: string) => read<{
      org_id: string; node_id: string; adapter_key: string; display_name: string; available: boolean; reported_at: string;
    }>(db.from('node_ide_adapters').select('org_id,node_id,adapter_key,display_name,available,reported_at').eq('org_id', orgId).limit(200)),
    workspaceOpenTargets: (orgId: string) => read<{
      org_id: string; node_id: string; node_name: string; project_id: string; project_name: string; has_folder: boolean; node_connected: boolean;
    }>(db.from('workspace_open_targets').select('org_id,node_id,node_name,project_id,project_name,has_folder,node_connected').eq('org_id', orgId).limit(200)),
  };
}


export type { ObserverMessageRow, ObserverThreadRow } from './observer-view';
export type Reader = ReturnType<typeof createReader>;

/** Approvals with no decision yet, for the approvals column. */
export function pendingApprovals(approvals: readonly Approval[], decisions: readonly ApprovalDecision[]): Approval[] {
  const decided = new Set(decisions.map((d) => d.approval_id));
  return approvals.filter((a) => !decided.has(a.id));
}

/** Pending approvals per worker, from the workerId the requesting worker put in the action. */
export function pendingByWorker(approvals: readonly Approval[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const a of approvals) {
    const id = typeof a.action.workerId === 'string' ? a.action.workerId : null;
    if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

/** Projects a node is currently working on (it has a session there that is not switched off). */
export function nodeProjectIds(nodeId: string, sessions: readonly SessionRef[]): Set<string> {
  return new Set(sessions.filter((s) => s.node_id === nodeId && s.status !== 'offline').map((s) => s.project_id));
}

/** Chat streams that belong to one worker's sessions. */
export function streamsForWorker(workerId: string, streams: readonly ChatStream[], sessions: readonly SessionRef[]): ChatStream[] {
  const mine = new Set(sessions.filter((s) => s.worker_id === workerId).map((s) => s.id));
  return streams.filter((s) => s.session_id !== null && mine.has(s.session_id));
}
